import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Stethoscope,
  Trash2,
  Upload,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { LIVE_CHAT_URL, WHATSAPP_URL } from "@/config/site";
import WhatsAppAccessModal from "@/components/ui/WhatsAppAccessModal";
import { shouldOpenWhatsAppDirectly } from "@/lib/device";

const REFERRAL_TYPES = [
  {
    id: "clinician",
    title: "Clinician Referral",
    description: "For dentists and clinicians referring specialist surgical cases.",
    icon: Stethoscope,
  },
  {
    id: "self",
    title: "Self-Referral",
    description: "For patients who want a specialist opinion directly.",
    icon: UserRound,
  },
];

const TREATMENT_OPTIONS = [
  "Dental Implants",
  "Full Arch Reconstruction",
  "Bone Grafting",
  "Gingival Grafting",
  "Sinus Lifting",
  "Surgical Extractions",
  "Apicectomy",
  "Complex Endodontic / Root Treatment",
  "Multiple / Unsure - Need Triage",
];

const ACCEPTED_FILE_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".rtf",
  ".txt",
  ".csv",
  ".xls",
  ".xlsx",
  ".odt",
  ".ods",
  ".ppt",
  ".pptx",
  ".jpg",
  ".jpeg",
  ".png",
  ".gif",
  ".bmp",
  ".webp",
  ".tif",
  ".tiff",
  ".heic",
  ".heif",
  ".dcm",
  ".dicom",
  ".zip",
  ".rar",
  ".7z",
].join(",");

const MAX_ATTACHMENTS = 12;

const INITIAL_FORM_DATA = {
  referralType: "",
  isUrgent: false,
  patientFirstName: "",
  patientLastName: "",
  patientDateOfBirth: "",
  patientPhone: "",
  patientEmail: "",
  treatmentRequested: "",
  referralReason: "",
  clinicalSummary: "",
  patientMessage: "",
  referrerName: "",
  referrerEmail: "",
  referrerPhone: "",
  practiceName: "",
  gdcNumber: "",
  consentToShare: false,
};

const STEP_TITLES = [
  "Referral Pathway",
  "Patient Details",
  "Clinical Details",
  "Referrer & Submit",
];

function StepIndicator({ steps, activeStep, onPrevious, onNext, isPreviousDisabled, isNextDisabled }) {
  const progressPercent = ((activeStep + 1) / steps.length) * 100;
  const mobileStepTitles = ["Referral", "Patient", "Clinical", "Submit"];
  const currentMobileTitle = mobileStepTitles[activeStep] || steps[activeStep];

  return (
    <>
      <div className="md:hidden rounded-2xl border border-stone-200 bg-white p-2.5">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={onPrevious}
            disabled={isPreviousDisabled}
            className="inline-flex h-9 w-20 items-center justify-center gap-1 rounded-full border border-stone-200 px-2 font-body text-xs font-medium text-charcoal transition-colors hover:border-stone-300 hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-45"
            data-testid="referral-step-prev"
          >
            <ChevronLeft size={14} strokeWidth={1.8} />
            Prev
          </button>

          <div className="min-w-0 flex-1 px-1">
            <div className="relative flex items-center justify-center">
              <p className="whitespace-nowrap px-8 text-center font-body text-xs font-semibold text-charcoal">
                {currentMobileTitle}
              </p>
              <p className="absolute right-0 font-accent text-[10px] uppercase tracking-[0.14em] text-stone-500">
                {activeStep + 1}/{steps.length}
              </p>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-stone-200">
              <div
                className="h-full rounded-full bg-charcoal transition-all duration-300"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={onNext}
            disabled={isNextDisabled}
            className="inline-flex h-9 w-20 items-center justify-center gap-1 rounded-full border border-charcoal bg-charcoal px-2 font-body text-xs font-medium text-warm-white transition-colors hover:bg-charcoal-light disabled:cursor-not-allowed disabled:opacity-45"
            data-testid="referral-step-next"
          >
            Next
            <ChevronRight size={14} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="hidden grid-cols-2 gap-3 sm:grid-cols-4 md:grid">
        {steps.map((stepLabel, index) => {
          const isComplete = activeStep > index;
          const isActive = activeStep === index;

          return (
            <div
              key={stepLabel}
              className={`rounded-2xl border px-4 py-3 transition-all ${
                isActive
                  ? "border-charcoal bg-charcoal text-warm-white"
                  : isComplete
                    ? "border-accent-gold/60 bg-accent-gold/10 text-charcoal"
                    : "border-stone-200 bg-white text-stone-500"
              }`}
            >
              <div className="mb-1 flex items-center gap-2">
                <span
                  className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${
                    isActive
                      ? "bg-warm-white text-charcoal"
                      : isComplete
                        ? "bg-accent-gold text-charcoal"
                        : "bg-stone-100 text-stone-500"
                  }`}
                >
                  {index + 1}
                </span>
                <span className="font-accent text-[11px] uppercase tracking-widest">Step</span>
              </div>
              <p className="font-body text-sm leading-snug">{stepLabel}</p>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ErrorMessage({ message }) {
  if (!message) return null;
  return <p className="mt-1 font-body text-xs text-red-600">{message}</p>;
}

export default function Referrals() {
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isWhatsAppModalOpen, setIsWhatsAppModalOpen] = useState(false);

  const isClinicianReferral = formData.referralType === "clinician";

  const summaryItems = useMemo(
    () => [
      { label: "Pathway", value: isClinicianReferral ? "Clinician Referral" : "Self-Referral" },
      { label: "Urgency", value: formData.isUrgent ? "Urgent" : "Standard" },
      {
        label: "Patient",
        value: [formData.patientFirstName, formData.patientLastName].filter(Boolean).join(" ") || "-",
      },
      { label: "Treatment", value: formData.treatmentRequested || "-" },
      {
        label: "Referrer",
        value: isClinicianReferral ? formData.referrerName || "-" : "Patient (Self-referral)",
      },
      { label: "Practice", value: isClinicianReferral ? formData.practiceName || "-" : "Self-referral" },
      { label: "Attachments", value: String(files.length) },
    ],
    [files.length, formData, isClinicianReferral],
  );

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const validateStep = (targetStep) => {
    const nextErrors = {};

    if (targetStep === 0) {
      if (!formData.referralType) {
        nextErrors.referralType = "Select Clinician Referral or Self-Referral.";
      }
    }

    if (targetStep === 1) {
      if (!formData.patientFirstName.trim()) nextErrors.patientFirstName = "First name is required.";
      if (!formData.patientLastName.trim()) nextErrors.patientLastName = "Last name is required.";
      if (!formData.patientDateOfBirth.trim()) nextErrors.patientDateOfBirth = "Date of birth is required.";
      if (!formData.patientPhone.trim()) nextErrors.patientPhone = "Phone number is required.";
      if (!formData.patientEmail.trim()) {
        nextErrors.patientEmail = "Patient email is required.";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.patientEmail.trim())) {
        nextErrors.patientEmail = "Enter a valid patient email address.";
      }
    }

    if (targetStep === 2) {
      if (!formData.treatmentRequested.trim()) nextErrors.treatmentRequested = "Select a treatment type.";
      if (!formData.referralReason.trim()) nextErrors.referralReason = "Add the referral reason.";
    }

    if (targetStep === 3) {
      if (isClinicianReferral) {
        if (!formData.referrerName.trim()) nextErrors.referrerName = "Referrer name is required.";
        if (!formData.referrerEmail.trim()) nextErrors.referrerEmail = "Referrer email is required.";
        if (!formData.practiceName.trim()) nextErrors.practiceName = "Practice name is required.";
      }

      if (!formData.consentToShare) {
        nextErrors.consentToShare = "Consent is required before submission.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const nextStep = () => {
    if (!validateStep(step)) return;
    setStep((prev) => Math.min(prev + 1, STEP_TITLES.length - 1));
  };

  const previousStep = () => {
    setStep((prev) => Math.max(prev - 1, 0));
  };

  const handleWhatsAppClick = (event) => {
    if (shouldOpenWhatsAppDirectly()) return;
    event.preventDefault();
    setIsWhatsAppModalOpen(true);
  };

  const onFilesSelected = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (!selectedFiles.length) return;

    const dedupeMap = new Map(files.map((file) => [`${file.name}:${file.size}`, file]));

    for (const file of selectedFiles) {
      dedupeMap.set(`${file.name}:${file.size}`, file);
    }

    const merged = Array.from(dedupeMap.values());
    if (merged.length > MAX_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_ATTACHMENTS} attachments allowed.`);
      setFiles(merged.slice(0, MAX_ATTACHMENTS));
    } else {
      setFiles(merged);
    }

    event.target.value = "";
  };

  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  const submitReferral = async (event) => {
    event.preventDefault();

    if (!validateStep(3)) {
      setStep(3);
      return;
    }

    const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
    const endpoint = `${apiBase}/api/referrals`;

    const payload = new FormData();
    payload.append("referral_type", formData.referralType);
    payload.append("is_urgent", String(formData.isUrgent));
    payload.append("treatment_requested", formData.treatmentRequested.trim());
    payload.append("referral_reason", formData.referralReason.trim());
    payload.append("clinical_summary", formData.clinicalSummary.trim());
    payload.append("patient_first_name", formData.patientFirstName.trim());
    payload.append("patient_last_name", formData.patientLastName.trim());
    payload.append("patient_date_of_birth", formData.patientDateOfBirth.trim());
    payload.append("patient_phone", formData.patientPhone.trim());
    payload.append("patient_email", formData.patientEmail.trim());
    payload.append("patient_message", formData.patientMessage.trim());
    payload.append("consent_to_share", String(formData.consentToShare));

    payload.append("referrer_name", formData.referrerName.trim());
    payload.append("referrer_email", formData.referrerEmail.trim());
    payload.append("referrer_phone", formData.referrerPhone.trim());
    payload.append("practice_name", formData.practiceName.trim());
    payload.append("gdc_number", formData.gdcNumber.trim());

    files.forEach((file) => payload.append("files", file));

    setIsSubmitting(true);

    try {
      const response = await fetch(endpoint, {
        method: "POST",
        body: payload,
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        const message = result?.detail || "Unable to submit referral right now.";
        throw new Error(message);
      }

      toast.success("Referral sent", {
        description: "Our specialist team will respond the same day.",
      });

      setFormData(INITIAL_FORM_DATA);
      setFiles([]);
      setErrors({});
      setStep(0);
    } catch (error) {
      toast.error("Submission failed", {
        description: error.message || "Please try again or contact us directly.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pb-mobile-bar" data-testid="referrals-page">
      <section className="relative overflow-hidden bg-gradient-to-b from-stone-100 via-warm-white to-warm-white py-10 md:py-14 lg:py-16" data-testid="referrals-hero">
        <div className="absolute right-0 top-0 h-64 w-64 -translate-y-1/3 translate-x-1/4 rounded-full bg-accent-gold/20 blur-3xl" />
        <div className="absolute bottom-0 left-0 h-72 w-72 -translate-x-1/3 translate-y-1/3 rounded-full bg-stone-200/60 blur-3xl" />

        <div className="container-luxury relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end"
          >
            <div className="max-w-3xl">
              <p className="text-caption mb-3 text-accent-gold">Specialist Referrals</p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-tight text-charcoal mb-4">
                Refer Directly to Our Specialist Team
              </h1>
              <p className="text-body-large">
                One guided referral form for both clinicians and self-referrals. Share case details, attach imaging and
                notes, and our team will respond the same day.
              </p>
            </div>

            <div className="rounded-3xl border border-charcoal bg-charcoal p-5 text-warm-white md:p-6">
              <h3 className="font-heading text-2xl">Need Help Right Now?</h3>
              <p className="mt-2 font-body text-sm text-stone-300">
                Same Day Response. Use the fastest route below for immediate triage.
              </p>
              <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <a
                  href={LIVE_CHAT_URL}
                  data-testid="referral-live-chat-link"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-warm-white px-4 py-3 font-body text-sm font-medium text-charcoal transition-all hover:bg-stone-100"
                >
                  Live Chat
                  <ArrowRight size={16} />
                </a>
                <a
                  href={WHATSAPP_URL}
                  onClick={handleWhatsAppClick}
                  data-testid="referral-whatsapp-link"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#25D366] px-4 py-3 font-body text-sm font-medium text-white transition-all hover:bg-[#20BD5A]"
                >
                  WhatsApp
                  <MessageCircle size={16} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pt-6 pb-16 md:pt-8 md:pb-20 lg:pt-10 lg:pb-24" data-testid="referrals-form-section">
        <div className="container-luxury">
          <StepIndicator
            steps={STEP_TITLES}
            activeStep={step}
            onPrevious={previousStep}
            onNext={nextStep}
            isPreviousDisabled={step === 0}
            isNextDisabled={step >= STEP_TITLES.length - 1}
          />

          <div className="mt-5 md:mt-6 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="rounded-3xl border border-stone-200 bg-white p-6 md:p-8"
            >
              <form onSubmit={submitReferral} className="space-y-8" noValidate>
                <AnimatePresence mode="wait">
                  {step === 0 && (
                    <motion.div
                      key="step-0"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-subheadline mb-2">Choose Referral Pathway</h2>
                        <p className="text-body">Select the route that matches who is submitting.</p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        {REFERRAL_TYPES.map((type) => {
                          const Icon = type.icon;
                          const isSelected = formData.referralType === type.id;

                          return (
                            <button
                              key={type.id}
                              type="button"
                              data-testid={`referral-type-${type.id}`}
                              onClick={() => updateField("referralType", type.id)}
                              className={`rounded-2xl border p-5 text-left transition-all ${
                                isSelected
                                  ? "border-charcoal bg-charcoal text-warm-white"
                                  : "border-stone-200 bg-stone-50 hover:border-accent-gold/60"
                              }`}
                            >
                              <Icon size={22} className={isSelected ? "text-accent-gold" : "text-charcoal"} />
                              <h3 className="mt-3 font-heading text-xl">{type.title}</h3>
                              <p className={`mt-2 font-body text-sm ${isSelected ? "text-stone-300" : "text-stone-600"}`}>
                                {type.description}
                              </p>
                            </button>
                          );
                        })}
                      </div>
                      <ErrorMessage message={errors.referralType} />

                      <label className="group flex cursor-pointer items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4">
                        <input
                          type="checkbox"
                          checked={formData.isUrgent}
                          onChange={(event) => updateField("isUrgent", event.target.checked)}
                          className="mt-1 h-4 w-4 accent-charcoal"
                          data-testid="referral-urgent-checkbox"
                        />
                        <span>
                          <span className="font-body font-semibold text-charcoal">Mark as urgent</span>
                        </span>
                      </label>
                    </motion.div>
                  )}

                  {step === 1 && (
                    <motion.div
                      key="step-1"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-subheadline mb-2">Patient Details</h2>
                        <p className="text-body">We use these details to contact and triage the patient quickly.</p>
                      </div>

                      <div className="grid gap-4 md:grid-cols-2">
                        <div>
                          <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientFirstName">
                            First name *
                          </label>
                          <input
                            id="patientFirstName"
                            type="text"
                            value={formData.patientFirstName}
                            onChange={(event) => updateField("patientFirstName", event.target.value)}
                            data-testid="patient-first-name"
                            className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                          />
                          <ErrorMessage message={errors.patientFirstName} />
                        </div>

                        <div>
                          <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientLastName">
                            Last name *
                          </label>
                          <input
                            id="patientLastName"
                            type="text"
                            value={formData.patientLastName}
                            onChange={(event) => updateField("patientLastName", event.target.value)}
                            data-testid="patient-last-name"
                            className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                          />
                          <ErrorMessage message={errors.patientLastName} />
                        </div>

                        <div>
                          <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientDateOfBirth">
                            Date of birth *
                          </label>
                          <input
                            id="patientDateOfBirth"
                            type="date"
                            value={formData.patientDateOfBirth}
                            onChange={(event) => updateField("patientDateOfBirth", event.target.value)}
                            data-testid="patient-dob"
                            className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                          />
                          <ErrorMessage message={errors.patientDateOfBirth} />
                        </div>

                        <div>
                          <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientPhone">
                            Phone *
                          </label>
                          <input
                            id="patientPhone"
                            type="tel"
                            value={formData.patientPhone}
                            onChange={(event) => updateField("patientPhone", event.target.value)}
                            data-testid="patient-phone"
                            className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                          />
                          <ErrorMessage message={errors.patientPhone} />
                        </div>

                        <div className="md:col-span-2">
                          <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientEmail">
                            Email *
                          </label>
                          <input
                            id="patientEmail"
                            type="email"
                            value={formData.patientEmail}
                            onChange={(event) => updateField("patientEmail", event.target.value)}
                            data-testid="patient-email"
                            className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                          />
                          <ErrorMessage message={errors.patientEmail} />
                        </div>
                      </div>
                    </motion.div>
                  )}

                  {step === 2 && (
                    <motion.div
                      key="step-2"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-subheadline mb-2">Clinical Details</h2>
                        <p className="text-body">Share the treatment need and clinical context for specialist triage.</p>
                      </div>

                      <div>
                        <label className="font-body text-sm font-medium text-charcoal" htmlFor="treatmentRequested">
                          Treatment requested *
                        </label>
                        <select
                          id="treatmentRequested"
                          value={formData.treatmentRequested}
                          onChange={(event) => updateField("treatmentRequested", event.target.value)}
                          data-testid="treatment-requested"
                          className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                        >
                          <option value="">Select specialist treatment</option>
                          {TREATMENT_OPTIONS.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                        <ErrorMessage message={errors.treatmentRequested} />
                      </div>

                      <div>
                        <label className="font-body text-sm font-medium text-charcoal" htmlFor="referralReason">
                          Reason for referral *
                        </label>
                        <textarea
                          id="referralReason"
                          rows={5}
                          value={formData.referralReason}
                          onChange={(event) => updateField("referralReason", event.target.value)}
                          data-testid="referral-reason"
                          className="mt-2 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 font-body text-sm"
                          placeholder="Chief complaint, clinical findings, urgency, and requested specialist input."
                        />
                        <ErrorMessage message={errors.referralReason} />
                      </div>

                      <div>
                        <label className="font-body text-sm font-medium text-charcoal" htmlFor="clinicalSummary">
                          Clinical summary (optional)
                        </label>
                        <textarea
                          id="clinicalSummary"
                          rows={4}
                          value={formData.clinicalSummary}
                          onChange={(event) => updateField("clinicalSummary", event.target.value)}
                          data-testid="clinical-summary"
                          className="mt-2 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 font-body text-sm"
                          placeholder="History, medications, imaging context, prior treatment attempts, risks."
                        />
                      </div>

                      <div>
                        <label className="font-body text-sm font-medium text-charcoal" htmlFor="patientMessage">
                          Additional patient note (optional)
                        </label>
                        <textarea
                          id="patientMessage"
                          rows={3}
                          value={formData.patientMessage}
                          onChange={(event) => updateField("patientMessage", event.target.value)}
                          data-testid="patient-message"
                          className="mt-2 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 font-body text-sm"
                          placeholder="Any accessibility, anxiety, language, or scheduling preferences."
                        />
                      </div>
                    </motion.div>
                  )}

                  {step === 3 && (
                    <motion.div
                      key="step-3"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="space-y-5"
                    >
                      <div>
                        <h2 className="text-subheadline mb-2">Referrer Details & Submit</h2>
                        <p className="text-body">
                          {isClinicianReferral
                            ? "Capture referrer details for rapid communication with your practice."
                            : "Confirm your details and submit directly to the specialist team."}
                        </p>
                      </div>

                      {isClinicianReferral ? (
                        <div className="grid gap-4 md:grid-cols-2">
                          <div>
                            <label className="font-body text-sm font-medium text-charcoal" htmlFor="referrerName">
                              Referrer name *
                            </label>
                            <input
                              id="referrerName"
                              type="text"
                              value={formData.referrerName}
                              onChange={(event) => updateField("referrerName", event.target.value)}
                              data-testid="referrer-name"
                              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                            />
                            <ErrorMessage message={errors.referrerName} />
                          </div>

                          <div>
                            <label className="font-body text-sm font-medium text-charcoal" htmlFor="referrerEmail">
                              Referrer email *
                            </label>
                            <input
                              id="referrerEmail"
                              type="email"
                              value={formData.referrerEmail}
                              onChange={(event) => updateField("referrerEmail", event.target.value)}
                              data-testid="referrer-email"
                              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                            />
                            <ErrorMessage message={errors.referrerEmail} />
                          </div>

                          <div>
                            <label className="font-body text-sm font-medium text-charcoal" htmlFor="referrerPhone">
                              Referrer phone (optional)
                            </label>
                            <input
                              id="referrerPhone"
                              type="tel"
                              value={formData.referrerPhone}
                              onChange={(event) => updateField("referrerPhone", event.target.value)}
                              data-testid="referrer-phone"
                              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                            />
                          </div>

                          <div>
                            <label className="font-body text-sm font-medium text-charcoal" htmlFor="practiceName">
                              Practice name *
                            </label>
                            <input
                              id="practiceName"
                              type="text"
                              value={formData.practiceName}
                              onChange={(event) => updateField("practiceName", event.target.value)}
                              data-testid="practice-name"
                              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                            />
                            <ErrorMessage message={errors.practiceName} />
                          </div>

                          <div className="md:col-span-2">
                            <label className="font-body text-sm font-medium text-charcoal" htmlFor="gdcNumber">
                              GDC number (optional)
                            </label>
                            <input
                              id="gdcNumber"
                              type="text"
                              value={formData.gdcNumber}
                              onChange={(event) => updateField("gdcNumber", event.target.value)}
                              data-testid="gdc-number"
                              className="mt-2 h-12 w-full rounded-xl border border-stone-200 bg-stone-50 px-4 font-body text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4">
                          <p className="font-body text-sm text-stone-600">
                            You selected <strong>Self-Referral</strong>. Your patient details above will be used as
                            referrer details.
                          </p>
                        </div>
                      )}

                      <div>
                        <label className="font-body text-sm font-medium text-charcoal">Attach files (optional)</label>
                        <label
                          htmlFor="referral-files"
                          className="mt-2 flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-4 transition-colors hover:border-accent-gold"
                        >
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                            <Upload size={18} className="text-charcoal" />
                          </div>
                          <div>
                            <p className="font-body text-sm font-medium text-charcoal">
                              Upload X-rays, scans, letters, and clinical documents
                            </p>
                            <p className="font-body text-xs text-stone-500">
                              Up to {MAX_ATTACHMENTS} files. Common medical, image, and document formats supported.
                            </p>
                          </div>
                          <input
                            id="referral-files"
                            type="file"
                            multiple
                            accept={ACCEPTED_FILE_EXTENSIONS}
                            onChange={onFilesSelected}
                            data-testid="referral-file-upload"
                            className="sr-only"
                          />
                        </label>

                        {!!files.length && (
                          <ul className="mt-3 space-y-2">
                            {files.map((file, index) => (
                              <li
                                key={`${file.name}-${file.size}-${index}`}
                                className="flex items-center justify-between rounded-xl border border-stone-200 bg-white px-3 py-2"
                              >
                                <span className="font-body text-sm text-stone-600">
                                  {file.name} ({Math.max(1, Math.round(file.size / 1024))} KB)
                                </span>
                                <button
                                  type="button"
                                  onClick={() => removeFile(index)}
                                  data-testid={`remove-file-${index}`}
                                  className="text-stone-500 transition-colors hover:text-red-600"
                                  aria-label={`Remove ${file.name}`}
                                >
                                  <Trash2 size={16} />
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      <label className="flex items-start gap-3 rounded-2xl border border-stone-200 bg-stone-50 p-4">
                        <input
                          type="checkbox"
                          checked={formData.consentToShare}
                          onChange={(event) => updateField("consentToShare", event.target.checked)}
                          data-testid="consent-checkbox"
                          className="mt-1 h-4 w-4 accent-charcoal"
                        />
                        <span className="font-body text-sm text-stone-600">
                          I confirm I have consent and lawful basis to share patient data for referral and specialist
                          treatment planning under UK GDPR and clinical governance obligations.
                        </span>
                      </label>
                      <ErrorMessage message={errors.consentToShare} />
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-stone-200 pt-6">
                  <button
                    type="button"
                    onClick={previousStep}
                    disabled={step === 0}
                    className="btn-secondary disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid="referral-back-btn"
                  >
                    Back
                  </button>

                  {step < STEP_TITLES.length - 1 ? (
                    <button
                      type="button"
                      onClick={nextStep}
                      className="btn-primary inline-flex items-center gap-2"
                      data-testid="referral-next-btn"
                    >
                      Continue
                      <ChevronRight size={18} strokeWidth={1.5} />
                    </button>
                  ) : (
                    <button
                      type="submit"
                      className="btn-primary inline-flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-60"
                      disabled={isSubmitting}
                      data-testid="referral-submit-btn"
                    >
                      {isSubmitting ? "Submitting..." : "Submit Referral"}
                      {!isSubmitting && <ArrowRight size={18} strokeWidth={1.5} />}
                    </button>
                  )}
                </div>
              </form>
            </motion.div>

            <motion.aside
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-5 lg:sticky lg:top-24"
            >
              <div className="rounded-3xl border border-stone-200 bg-white p-6">
                <h3 className="font-heading text-2xl text-charcoal">Referral Summary</h3>
                <p className="mt-2 font-body text-sm text-stone-600">
                  Review before submitting. This updates as you complete each step.
                </p>

                <div className="mt-4 space-y-3">
                  {summaryItems.map((item) => (
                    <div key={item.label} className="flex items-center justify-between gap-3 border-b border-stone-100 pb-2">
                      <span className="font-body text-sm text-stone-500">{item.label}</span>
                      <span className="font-body text-sm font-medium text-charcoal text-right">{item.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-3xl border border-stone-200 bg-stone-50 p-6">
                <div className="flex items-start gap-3">
                  <AlertTriangle size={20} className="mt-0.5 text-accent-gold" />
                  <div>
                    <h4 className="font-body font-semibold text-charcoal">Clinical Note</h4>
                    <p className="mt-2 font-body text-sm text-stone-600">
                      This form is for specialist referral only and does not replace emergency triage.
                    </p>
                  </div>
                </div>
                <Link
                  to="/contact"
                  data-testid="referral-contact-link"
                  className="mt-4 inline-flex items-center gap-2 font-body text-sm font-medium text-charcoal link-underline"
                >
                  Need direct contact details?
                  <ChevronRight size={15} />
                </Link>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      <WhatsAppAccessModal
        open={isWhatsAppModalOpen}
        onClose={() => setIsWhatsAppModalOpen(false)}
      />
    </div>
  );
}
